import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useCanAccessCamera, useCanAccessMicrophone, useIsUsingFrontCamera, useMediaStream, useRequestMediaPermission, } from '../../state/mediaState';
import { isSupportedBrowser, useHasSupportedVideoCodec } from '../../utils';
import { useBroadcastUpdater } from '../../utils/media';
import { getStreamUrl } from '../../utils/url';
import { useStreamrTvBroadcaster } from '../../utils/useStreamrTvBroadcaster';
import { CodecNotSupported, GenericBrowserNotSupported, } from '../BrowserNotSupported';
import { Button } from '../Button';
import { AudioDeviceButton, VideoDeviceButton } from '../DeviceButton';
import { FullscreenButton } from '../FullscreenButton';
import { Hud } from '../Hud';
import { LiveIndicator } from '../LiveIndicator';
import { LoadingIndicator } from '../LoadingIndicator';
import { Stage } from '../Stage';
import { Video } from '../Video';
import { BlockedPermissions, MobileView, ViewToolbarShareButtons, ViewToolbarShareUrlButton, } from './View';
export function BroadcasterViewMobile({ streamPartId, }) {
    const broadcaster = useStreamrTvBroadcaster(streamPartId);
    const mediaStream = useMediaStream();
    const [broadcastStartedAt, setBroadcastStartedAt] = useState(undefined);
    const isBroadcasting = broadcastStartedAt !== undefined;
    useBroadcastUpdater({
        broadcaster,
        mediaStream,
        enabled: isBroadcasting,
    });
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const canAccessCamera = useCanAccessCamera();
    const canAccessMicrophone = useCanAccessMicrophone();
    const requestMediaPermission = useRequestMediaPermission();
    const hasTracks = useMemo(() => mediaStream.getTracks().length > 0, [mediaStream]);
    const canGoLive = hasTracks && !!broadcaster;
    const isUsingFrontCamera = useIsUsingFrontCamera(mediaStream);
    const [viewElement, setViewElement] = useState(null);
    if (!isSupportedBrowser()) {
        return _jsx(GenericBrowserNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !broadcaster }), _jsxs(MobileView, { innerRef: setViewElement, children: [_jsx(Stage, { children: (canAccessCamera || canAccessMicrophone) && (_jsx(Video, { fit: "cover", srcObject: mediaStream, autoPlay: true, playsInline: true, muted: true, mirrored: isUsingFrontCamera })) }), _jsxs(BroadcasterHud, { visible: true, children: [_jsxs(TopButtons, { children: [_jsxs("div", { children: [_jsx(ViewToolbarShareButtons, { children: _jsx(ViewToolbarShareUrlButton, { url: getStreamUrl(streamPartId) }) }), _jsx(LiveIndicator, { startedAt: broadcastStartedAt, streamPartId: streamPartId })] }), _jsx("div", { children: _jsx(FullscreenButton, { element: viewElement }) })] }), _jsx("div", { children: !canAccessCamera && !canAccessMicrophone && (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: () => {
                                            requestMediaPermission('both');
                                        }, children: "Allow microphone and\u00A0camera" }) })) }), _jsxs(Buttons, { children: [_jsx(AudioDeviceButton, { mediaStream: mediaStream }), _jsx(VideoDeviceButton, {}), _jsx("div", {}), _jsx(GoLiveButton, { type: "button", kind: isBroadcasting ? 'destructive' : 'primary', shape: "pill", onClick: () => {
                                            setBroadcastStartedAt((prev) => prev ? undefined : new Date());
                                        }, disabled: !canGoLive, children: isBroadcasting ? 'Pause' : 'Go Live' })] })] })] })] }));
}
const TopButtons = styled.div `
    display: flex;
    justify-content: space-between;

    button {
        pointer-events: all;
    }
`;
const Buttons = styled.div `
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    gap: 0.75rem;

    & > * {
        pointer-events: all;
    }
`;
const GoLiveButton = styled(Button) ``;
const BroadcasterHud = styled(Hud) `
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 1.25rem;
    box-sizing: border-box;
`;
